<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { __, np__ } from '@@/bits/intl'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import type { CommentId } from '@@/types'
import emitClickOnTouch from '@@/vuedirectives/emit_click_on_touch'
import { uniqueId } from 'es-toolkit/compat'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    commentIds: CommentId[]
    colorScheme: 'light' | 'dark'
    isButton: boolean
    usageContext?: 'postView' | 'postExpandedView'
  }>(),
  {
    usageContext: 'postView',
  },
)

const emit = defineEmits<{
  (name: 'show-comments-panel'): void
}>()

const vEmitClickOnTouch = emitClickOnTouch

const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const { isContainerSmallerThanTabletPortrait } = storeToRefs(surfaceContainerSizeStore)

const surfaceCommentsStore = useCommentsStore()
const { commentEntities } = storeToRefs(surfaceCommentsStore)

const rootEl = ref<HTMLDivElement>()

const isUsedInPostExpandedView = computed<boolean>(() => props.usageContext === 'postExpandedView')

const iconSize = computed<number>(() => {
  if (isUsedInPostExpandedView.value) {
    return isContainerSmallerThanTabletPortrait.value ? 24 : 20
  }
  return 18
})

const commentsListLength = computed<number>(() => {
  if (isAppUsing('commentModeration')) {
    const publishedCommentIds = props.commentIds.filter(
      (id) => !commentEntities.value[id as number].is_awaiting_approval,
    )
    return publishedCommentIds.length
  }
  return props.commentIds.length
})

const showCommentsPanel = (): void => {
  if (!props.isButton) return
  if (rootEl.value?.closest('.surface-post')?.classList.contains('noclick')) {
    // Prevent popping out the comment panel when the post is being dragged
    return
  }
  emit('show-comments-panel')
}

const wrapperElement = computed(() => {
  return props.isButton ? 'button' : 'div'
})
const buttonAriaDescribedby = computed(() => (props.isButton ? uniqueId('comment-button-') : ''))
</script>

<template>
  <component
    :is="wrapperElement"
    ref="rootEl"
    v-emit-click-on-touch
    :class="[
      'h-6.5',
      // reset button styles
      '-m-1 p-1 bg-transparent border-none outline-none',
      // focus-visible styles
      'focus-visible:ring-2 rounded',
      colorScheme === 'light' && 'focus-visible:ring-grape-500',
      colorScheme === 'dark' && 'focus-visible:ring-canary-500',
    ]"
    :aria-label="isButton ? __('Show comments for this post') : undefined"
    :aria-describedby="isButton ? buttonAriaDescribedby : undefined"
    data-testid="postRowCommentsButton"
    @click="showCommentsPanel"
  >
    <div
      :class="[
        'flex',
        {
          'items-end': !isUsedInPostExpandedView,
          'items-center': isUsedInPostExpandedView,
        },
        {
          'text-dark-text-200': colorScheme === 'light',
          'text-light-text-200': colorScheme === 'dark',
        },
      ]"
    >
      <OzIcon name="comment_outline" :size="iconSize" />
      <span
        aria-hidden="true"
        :class="[
          {
            'ms-0.5': !isUsedInPostExpandedView,
            'ms-1': isUsedInPostExpandedView,
          },
          {
            'text-12-16 font-sans': !isUsedInPostExpandedView,
            'text-body-small': isUsedInPostExpandedView,
          },
          {
            'text-dark-text-200': colorScheme === 'light',
            'text-light-text-200': colorScheme === 'dark',
          },
        ]"
        >{{ commentsListLength }}</span
      >
      <span :id="buttonAriaDescribedby" class="sr-only">{{
        np__(
          'Amount of comments in a post',
          'This post has %{commentsListLength} comment',
          'This post has %{commentsListLength} comments',
          commentsListLength,
          { commentsListLength },
        )
      }}</span>
    </div>
  </component>
</template>
